import { zodResolver } from "@hookform/resolvers/zod";
import { Check, ChevronsUpDown } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "src/components/form/form";
import { Input } from "src/components/input/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/input/Select";
import {
  RegisterEmployeeSchema,
  RegistrationFormValues,
} from "src/schemas/add-employee";
import { cn } from "src/utils";
import { StateList } from "src/utils/states";
import { DepartmentList } from "src/utils/DepartmentList";
import { Button } from "src/components/buttons/shadcn-button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "src/components/ui/Popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "src/components/ui/Command";
import { useDebounce } from "use-debounce";
import { Badge } from "src/components/ui/Badge";
import { ScrollArea } from "src/components/ui/ScrollArea";
import { Checkbox } from "src/components/input/CheckBoxNew";
import { useEmployees } from "../EmployeeList/hook/useFetchEmployees";
import { useRegistration } from "./hook/useRegistration";

interface SectionProps {
  className?: string;
  children: React.ReactNode;
  sectionTitle: string;
}

const Section = ({ className, children, sectionTitle }: SectionProps) => {
  return (
    <div
      className={cn(
        "rounded-md shadow-md p-2 my-2 flex flex-col gap-3",
        className,
      )}
    >
      <h1 className="text-lg font-semibold text-gray-800">{sectionTitle}</h1>
      <div className="flex-1">{children}</div>
    </div>
  );
};

const EmployeeRegisterForm = () => {
  const form = useForm<RegistrationFormValues>({
    resolver: zodResolver(RegisterEmployeeSchema),
    defaultValues: {
      full_name: "",
      date_of_birth: "",
      marital_status: "",
      gender: "",
      nationality: "Indian",
      phone_number: "",
      email: "",
      permanent_address: "",
      current_address: "",
      stateDistricts: [],
      pin: "",

      department: "",
      designation: "",
      date_of_joining: "",
      reporting_manager: null,
      role: "",
      employeement_type: "",
      duration: "",
      start_date: "",
      end_date: "",

      adharcard_number: "",
      pan_card_number: "",
      passport_number: "",

      bank_name: "",
      branch: "",
      bank_ac_number: "",
      ifsc_number: "",
      pf_number: "",
      uan_number: "",
      esic_uan: "",

      emergency_contact_name: "",
      emergency_contact_relationship: "",
      emergency_contact_number: "",
      emergency_contact_address: "",
    },
  });

  const [searchState, setSearchState] = useState("");

  const [search, setSearch] = useState("");
  const [manager, setManager] = useState("");

  const [debouncedSearch] = useDebounce(search, 500);

  const filter = {
    page: 1,
    pagesize: 10,
    name: debouncedSearch,
    employee_code: "",
  };

  const { data: employees = [], isLoading } = useEmployees(filter);

  const searchedEmployees = useMemo(() => {
    return employees;
  }, [employees]);

  const designation = form.watch("designation");

  const department = useWatch({
    control: form.control,
    name: "department",
  });

  const employmentType = useWatch({
    control: form.control,
    name: "employeement_type",
  });

  const { mutate: register, isPending } = useRegistration();

  const onSubmit = async (values: RegistrationFormValues) => {
    register(values, {
      onSuccess: () => {
        form.reset();
      },
    });
  };

  const filteredStates = useMemo(() => {
    if (!searchState.trim()) return StateList;

    return StateList.filter(
      (s) =>
        s.state.toLowerCase().includes(searchState.toLowerCase()) ||
        s.districts.some((d) =>
          d.toLowerCase().includes(searchState.toLowerCase()),
        ),
    ).map((s) => ({
      ...s,
      districts: s.districts.filter((d) =>
        d.toLowerCase().includes(searchState.toLowerCase()),
      ),
    }));
  }, [searchState]);

  useEffect(() => {
    if (designation === "Mission Director / Managing Director (MD)") {
      form.setValue("reporting_manager", null, { shouldValidate: true });
      form.setValue("role", "MD", { shouldValidate: true });
    } else if (designation === "Support Officer (SO)") {
      const mdUser = searchedEmployees.find(
        (emp) =>
          emp.designation === "Mission Director / Managing Director (MD)",
      );

      if (mdUser) {
        form.setValue("reporting_manager", Number(mdUser.id), {
          shouldValidate: true,
        });
      }
      form.setValue("role", "SO", { shouldValidate: true });
    } else {
      form.setValue("role", "");
    }
  }, [designation, searchedEmployees, form]);

  useEffect(() => {
    form.setValue(
      "duration",
      employmentType === "CONFIRMED" ? "Starting Date" : "",
    );
  }, [employmentType, form]);

  return (
    <div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Section sectionTitle="Personal Information">
            <div className="grid grid-cols-3 gap-4 w-[70%] mx-auto">
              <FormField
                control={form.control}
                name="full_name"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Full Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Full Name"
                        {...field}
                        value={field.value ?? ""}
                        className={
                          form.formState.errors.full_name
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        This is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phone_number"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Contact Number</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="1234567890"
                        {...field}
                        value={field.value ?? ""}
                        max={10}
                        className={
                          form.formState.errors.phone_number
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        This is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="example@gmail.com"
                        {...field}
                        value={field.value ?? ""}
                        className={
                          form.formState.errors.email ? "border-red-500" : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        This is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-3 gap-4 w-[70%] mx-auto my-3">
              <FormField
                control={form.control}
                name="date_of_birth"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Date Of Birth</FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        placeholder="MM-DD-YYYY"
                        {...field}
                        value={field.value ?? ""}
                        className={
                          form.formState.errors.date_of_birth
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        This is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="marital_status"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Marital Status</FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value ?? ""}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder="Select Marital Status"
                            className={
                              form.formState.errors.marital_status
                                ? "border-red-400"
                                : ""
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {["Single", "Married", "Divorced", "Widowed"].map(
                            (status) => (
                              <SelectItem
                                key={status}
                                value={status}
                                className="focus:bg-accent focus:text-foreground"
                              >
                                {status}
                              </SelectItem>
                            ),
                          )}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-gray-400">
                        This is Optional
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="gender"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Gender</FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value ?? ""}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder="Select Gender"
                            className={
                              form.formState.errors.gender
                                ? "border-red-500"
                                : ""
                            }
                          />
                          <SelectContent>
                            {["Male", "Female", "Other"].map((gender) => (
                              <SelectItem
                                key={gender}
                                value={gender}
                                className="focus:bg-accent focus:text-foreground"
                              >
                                {gender}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </SelectTrigger>
                      </Select>
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        This is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="pin"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Pin Code</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter Employee Pin Code"
                        {...field}
                        value={field.value ?? ""}
                        className={
                          form.formState.errors.pin ? "border-red-500" : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        This is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="nationality"
                defaultValue="Indian"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nationality</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Nationality"
                        {...field}
                        value="Indian"
                        readOnly
                        className={
                          form.formState.errors.nationality
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
            <div className="grid grid-cols-2 w-[70%] mx-auto my-3 gap-4">
              <FormField
                control={form.control}
                name="permanent_address"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Permanent Address</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter Complete Address"
                        {...field}
                        value={field.value ?? ""}
                        className={
                          form.formState.errors.permanent_address
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        This is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="current_address"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Current Address</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter Complete Address"
                        {...field}
                        value={field.value ?? ""}
                        className={
                          form.formState.errors.current_address
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        This is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />
            </div>
          </Section>
          <Section sectionTitle="Employment Details">
            <div className="grid grid-cols-3 gap-4 w-[70%] mx-auto my-3">
              <FormField
                control={form.control}
                name="employeement_type"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Employment Type</FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select Employment Type" />
                        </SelectTrigger>
                        <SelectContent>
                          {[
                            "INTERN PROBATION",
                            "EXTENDED INTERN PROBATION",
                            "PROBATION",
                            "EXTENDED PROBATION",
                            "CONFIRMED",
                          ].map((type) => (
                            <SelectItem
                              key={type}
                              value={type}
                              className="focus:bg-accent focus:text-foreground"
                            >
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        This is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />
              {/* <FormField
                control={form.control}
                name="duration"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Duration</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        readOnly={form.watch("employment_type") === "CONFIRMED"}
                        placeholder={
                          form.watch("employment_type") === "CONFIRMED"
                            ? "Confirmed Date"
                            : "Select Duration"
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              /> */}
              {form.watch("employeement_type") !== "CONFIRMED" && (
                <FormField
                  control={form.control}
                  name="duration"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Duration</FormLabel>
                      <FormControl>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value ?? ""}
                        >
                          <SelectTrigger>
                            <SelectValue
                              placeholder="Select Duration"
                              className={
                                form.formState.errors.duration
                                  ? "border-red-500"
                                  : ""
                              }
                            />
                            <SelectContent>
                              {["6 Months", "9 Months", "12 Months"].map(
                                (d) => (
                                  <SelectItem
                                    key={d}
                                    value={d}
                                    className="focus:bg-accent focus:text-foreground"
                                  >
                                    {d}
                                  </SelectItem>
                                ),
                              )}
                            </SelectContent>
                          </SelectTrigger>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <div className="grid grid-cols-2 gap-4">
                {/* Show Start Date only if NOT confirmed */}
                {form.watch("employeement_type") !== "CONFIRMED" && (
                  <FormField
                    control={form.control}
                    name="start_date"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Start Date</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {/* End Date / Confirmed Date */}
                <FormField
                  control={form.control}
                  name="end_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {form.watch("employeement_type") === "CONFIRMED"
                          ? "Confirmed Date"
                          : "End Date"}
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="date"
                          {...field}
                          value={field.value || ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="department"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Department</FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={(value) => {
                          field.onChange(value);
                          form.setValue("designation", ""); // reset designation if dept changes
                        }}
                        value={field.value}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select Department" />
                        </SelectTrigger>
                        <SelectContent>
                          {DepartmentList.map((dept) => (
                            <SelectItem
                              key={dept.department}
                              value={dept.department}
                              className="focus:bg-accent focus:text-foreground"
                            >
                              {dept.department}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        Department is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="designation"
                render={({ field }) => {
                  const selectedDept = DepartmentList.find(
                    (dept) => dept.department === form.watch("department"),
                  );

                  return (
                    <FormItem>
                      <FormLabel>Designation</FormLabel>
                      <FormControl>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                          disabled={!selectedDept}
                        >
                          <SelectTrigger>
                            <SelectValue
                              placeholder={
                                selectedDept
                                  ? "Select Designation"
                                  : "Select Department first"
                              }
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {selectedDept?.designations.map((des) => (
                              <SelectItem
                                key={des}
                                value={des}
                                className="focus:bg-accent focus:text-foreground"
                              >
                                {des}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                      {!field.value && (
                        <FormDescription className="text-red-400">
                          Designation is required
                        </FormDescription>
                      )}
                    </FormItem>
                  );
                }}
              />
            </div>

            <div className="grid grid-cols-3 gap-4 w-[70%] mx-auto my-3">
              {designation !== "Mission Director / Managing Director (MD)" && (
                <FormField
                  control={form.control}
                  name="reporting_manager"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Reporting Officer</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant="outline"
                              role="combobox"
                              className={cn(
                                "w-full justify-between",
                                !field.value && "text-muted-foreground",
                                form.formState.errors.reporting_manager &&
                                  "border-red-500",
                              )}
                            >
                              {field.value
                                ? manager
                                : "Search & select manager..."}
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent>
                          <Command>
                            <CommandInput
                              placeholder="Search employee..."
                              value={search}
                              onValueChange={setSearch}
                            />
                            <CommandList>
                              {searchedEmployees.length === 0 &&
                                debouncedSearch && (
                                  <CommandEmpty>
                                    No employees found.
                                  </CommandEmpty>
                                )}
                              {isLoading ? (
                                <CommandEmpty>Loading...</CommandEmpty>
                              ) : (
                                <CommandGroup>
                                  {searchedEmployees.map((emp) => (
                                    <CommandItem
                                      key={emp.id}
                                      value={emp.full_name}
                                      onSelect={() => {
                                        setManager(emp.full_name);
                                        form.setValue(
                                          "reporting_manager",
                                          emp.id,
                                          {
                                            shouldValidate: true,
                                          },
                                        );
                                      }}
                                    >
                                      <Check
                                        className={cn(
                                          "mr-2 h-4 w-4",
                                          emp.id === field.value
                                            ? "opacity-100"
                                            : "opacity-0",
                                        )}
                                      />
                                      {emp.full_name}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              )}
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="role"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Dashboard Role</FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value ?? ""}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder="Select Role"
                            className={
                              form.formState.errors.role ? "border-red-500" : ""
                            }
                          />
                          <SelectContent>
                            {[
                              "MD",
                              "SO",
                              "ZWO",
                              "CHRWO",
                              "HRWO",
                              "CITWO",
                              "ITWO",
                              "FDEx",
                              "RWO",
                              "DWO",
                              "AWO",
                            ].map((role) => (
                              <SelectItem
                                key={role}
                                value={role}
                                className="focus:bg-accent focus:text-foreground"
                              >
                                {role}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </SelectTrigger>
                      </Select>
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        This is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />

              {department === "Business Development" && (
                <FormField
                  control={form.control}
                  name="stateDistricts"
                  defaultValue={[]}
                  render={({ field }) => {
                    const selectedItems = field.value
                      ? field.value.flatMap((s) =>
                          s.districts.length > 0
                            ? s.districts.map((d) => `${s.state}: ${d}`)
                            : [s.state],
                        )
                      : [];

                    return (
                      <FormItem>
                        <FormLabel>States & Districts</FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              className="w-full justify-between overflow-hidden"
                            >
                              {selectedItems.length > 0 ? (
                                <div className="flex items-center gap-1 flex-wrap">
                                  {selectedItems.slice(0, 2).map((item) => (
                                    <Badge
                                      key={item}
                                      className="max-w-[100px] truncate text-foreground px-2"
                                    >
                                      {item}
                                    </Badge>
                                  ))}
                                  {selectedItems.length > 2 && (
                                    <Badge className="text-xs text-foreground">
                                      +{selectedItems.length - 2} more
                                    </Badge>
                                  )}
                                </div>
                              ) : (
                                <span className="text-muted-foreground">
                                  Select States & Districts
                                </span>
                              )}
                              <ChevronsUpDown className="h-4 w-4 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[350px] p-3">
                            <Input
                              placeholder="Search state or district..."
                              value={searchState}
                              onChange={(e) => setSearchState(e.target.value)}
                              className="mb-3"
                            />
                            <ScrollArea className="h-[400px] pr-3">
                              <div className="space-y-4">
                                {filteredStates.map((state) => {
                                  const stateEntry =
                                    field.value.find(
                                      (s) => s.state === state.state,
                                    ) || null;
                                  const isStateSelected = !!stateEntry;

                                  return (
                                    <div
                                      key={state.state}
                                      className="border rounded-lg p-2 bg-muted/40"
                                    >
                                      <div className="flex items-center justify-between">
                                        <label className="font-medium">
                                          {state.state}
                                        </label>
                                        <Checkbox
                                          checked={isStateSelected}
                                          onCheckedChange={(checked) => {
                                            if (checked) {
                                              field.onChange([
                                                ...field.value,
                                                {
                                                  state: state.state,
                                                  districts: [],
                                                },
                                              ]);
                                            } else {
                                              field.onChange(
                                                field.value.filter(
                                                  (s) =>
                                                    s.state !== state.state,
                                                ),
                                              );
                                            }
                                          }}
                                        />
                                      </div>

                                      {isStateSelected &&
                                        state.districts.length > 0 && (
                                          <div className="grid grid-cols-2 gap-1 mt-2 pl-2">
                                            {state.districts.map((district) => {
                                              const isDistrictSelected =
                                                stateEntry?.districts.includes(
                                                  district,
                                                ) ?? false;

                                              return (
                                                <label
                                                  key={district}
                                                  className="flex items-center space-x-2"
                                                >
                                                  <Checkbox
                                                    checked={isDistrictSelected}
                                                    onCheckedChange={(
                                                      checked,
                                                    ) => {
                                                      const newStates = [
                                                        ...field.value,
                                                      ];
                                                      const idx =
                                                        newStates.findIndex(
                                                          (s) =>
                                                            s.state ===
                                                            state.state,
                                                        );

                                                      if (idx !== -1) {
                                                        let districts =
                                                          newStates[idx]
                                                            .districts || [];

                                                        if (checked) {
                                                          districts = [
                                                            ...districts,
                                                            district,
                                                          ];
                                                        } else {
                                                          districts =
                                                            districts.filter(
                                                              (d) =>
                                                                d !== district,
                                                            );
                                                        }

                                                        newStates[idx] = {
                                                          ...newStates[idx],
                                                          districts,
                                                        };
                                                        field.onChange(
                                                          newStates,
                                                        );
                                                      }
                                                    }}
                                                  />
                                                  <span className="text-sm">
                                                    {district}
                                                  </span>
                                                </label>
                                              );
                                            })}
                                          </div>
                                        )}
                                    </div>
                                  );
                                })}
                              </div>
                            </ScrollArea>
                          </PopoverContent>
                        </Popover>

                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              )}
            </div>

            <div className="grid grid-cols-3 gap-4 w-[70%] mx-auto mt-4">
              <FormField
                control={form.control}
                name="date_of_joining"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Date of Joining</FormLabel>
                    <FormControl>
                      <Input
                        type="date"
                        {...field}
                        value={field.value ?? ""}
                        className={
                          form.formState.errors.date_of_joining
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        This is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />
            </div>
          </Section>
          <Section sectionTitle="Identification Details">
            <div className="grid grid-cols-3 gap-4 w-[70%] mx-auto my-3">
              <FormField
                control={form.control}
                name="adharcard_number"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Aadhaar Card Number</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter Aadhaar Number"
                        {...field}
                        value={field.value ?? ""}
                        className={
                          form.formState.errors.adharcard_number
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        This is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="pan_card_number"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>PAN Card Number</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="ABCDE1234F"
                        {...field}
                        value={field.value ?? ""}
                        className={
                          form.formState.errors.pan_card_number
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        This is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="passport_number"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Passport Number</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter Passport Number"
                        {...field}
                        className={
                          form.formState.errors.passport_number
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-gray-400">
                        This is optional
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />
            </div>
          </Section>
          <Section sectionTitle="Banking Details">
            <div className="grid grid-cols-4 gap-4 w-[70%] mx-auto my-3">
              <FormField
                control={form.control}
                name="bank_ac_number"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Bank Account Number</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter Bank Account Number"
                        {...field}
                        value={field.value ?? ""}
                        className={
                          form.formState.errors.bank_ac_number
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        This is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="ifsc_number"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>IFSC Code</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="SBIN0012345"
                        {...field}
                        value={field.value ?? ""}
                        className={
                          form.formState.errors.ifsc_number
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        This is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="bank_name"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Bank Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="SBI / HDFC / ICICI"
                        {...field}
                        className={
                          form.formState.errors.bank_name
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        This is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="branch"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Branch</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Borpool / Jorhat"
                        {...field}
                        className={
                          form.formState.errors.branch ? "border-red-500" : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-red-400">
                        This is required
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-3 gap-4 w-[70%] mx-auto">
              <FormField
                control={form.control}
                name="pf_number"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>PF Number</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter PF UAN"
                        {...field}
                        className={
                          form.formState.errors.pf_number
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-gray-400">
                        This is Optional
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="uan_number"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>UAN Number</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter PF UAN"
                        {...field}
                        className={
                          form.formState.errors.uan_number
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-gray-400">
                        This is Optional
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="esic_uan"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>ESIC UAN</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter ESIC UAN"
                        {...field}
                        className={
                          form.formState.errors.esic_uan ? "border-red-500" : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                    {!field.value && (
                      <FormDescription className="text-gray-400">
                        This is Optional
                      </FormDescription>
                    )}
                  </FormItem>
                )}
              />
            </div>
          </Section>

          <Section sectionTitle="Emergency Contact Details">
            <div className="grid grid-cols-4 gap-4 w-[70%] mx-auto my-3">
              <FormField
                control={form.control}
                name="emergency_contact_name"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Emergency Contact Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="John Doe"
                        {...field}
                        className={
                          form.formState.errors.emergency_contact_name
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="emergency_contact_relationship"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Relationship</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Sister / Brother / Spouse"
                        {...field}
                        className={
                          form.formState.errors.emergency_contact_relationship
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="emergency_contact_number"
                defaultValue=""
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Emergency Contact Number</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="9876543210"
                        {...field}
                        className={
                          form.formState.errors.emergency_contact_number
                            ? "border-red-500"
                            : ""
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="emergency_contact_address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Emergency Contact Address</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter Emergency Contact Address"
                        {...field}
                        value={field.value ?? ""}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </Section>

          <div className="my-3 flex justify-center items-center p-4">
            <Button
              disabled={form.formState.isSubmitting || isPending}
              className="shadow-md hover:shadow-gray-600  bg-primary hover:bg-accent transition-all duration-300 ease-in-out"
              onClick={() => console.log(form.formState.errors)}
            >
              {form.formState.isSubmitting || isPending ? (
                <span className="loading loading-dots loading-md"></span>
              ) : (
                "Add Employee"
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default EmployeeRegisterForm;
